import { useCallback, useEffect, useRef, useState } from "react";
import type { ForecastChange, InstanceChangeDays, InstanceForecast, InstanceSnapshot, ResolvedSection } from "@keydials/api-client";
import { useWorkspaceServices } from "../../context";
import { DEFAULT_AHEAD_DAYS, DEFAULT_PRESET_DAYS, daysAgo, daysAhead, endOfDay, type CompareDirection } from "./compareModel";

/** Which dial the card follows. Now unless the reader picks the Then chip. */
export type CompareFollows = "now" | "then";

export interface CompareMode {
  active: boolean;
  /** KEY-890: back to Then, or ahead to how it will be. */
  direction: CompareDirection;
  /** Ahead only: what changes before the day, and a days dial's days from it. */
  changes: ForecastChange[] | null;
  days: ResolvedSection[] | null;
  /** The Then day, `yyyy-mm-dd`, read as the end of that day. */
  day: string;
  status: "idle" | "loading" | "ready" | "error";
  /** The replayed face for `day`. Kept while a newer day loads or after a failed one, so Then never blanks. */
  snapshot: InstanceSnapshot | null;
  follows: CompareFollows;
  enter: (direction?: CompareDirection) => void;
  leave: () => void;
  setDay: (day: string) => void;
  /**
   * The scrubber's setter: `day` (and the date field) follow at once, but the snapshot is only asked for once
   * the pointer has rested for {@link SCRUB_REST_MS}. Dragging across a year must not fire a year of requests.
   */
  previewDay: (day: string) => void;
  setFollows: (f: CompareFollows) => void;
  retry: () => void;
  /** The days this dial changed, for the scrubber's ticks. Null until loaded, or if they couldn't be. */
  changeDays: InstanceChangeDays | null;
}

/** How long the pointer must rest on a day before Then is replayed for it. */
export const SCRUB_REST_MS = 250;

/**
 * The Then day survives leaving and re-entering compare mode, and moving between dials, for the rest of
 * the session — so a second look is instant rather than back at the default. In memory, not storage: this
 * is a session's working state on a shared tablet, not a preference.
 */
let rememberedDay: string | null = null;
let rememberedAheadDay: string | null = null;

/** Snapshots already fetched, per dial and day. Scrubbing back to a day already seen costs nothing. */
const snapshotCache = new Map<string, InstanceSnapshot>();
/** Look ahead's answers, per dial and day — short-lived on purpose: the future moves as people act, so a forecast
 * is kept only for this compare session (cleared on leaving). */
const forecastCache = new Map<string, InstanceForecast>();

/** The change days per dial, for the session. They only grow, and a stale tick or two is harmless. */
const changeDaysCache = new Map<string, InstanceChangeDays>();

/** Test hook: forget the session's day and every cached snapshot. */
export function resetCompareSession() {
  rememberedDay = null;
  rememberedAheadDay = null;
  snapshotCache.clear();
  forecastCache.clear();
  changeDaysCache.clear();
}

export function useCompareMode(instanceId: string, enabled: boolean): CompareMode {
  const { api, getAccessToken } = useWorkspaceServices();
  const [active, setActive] = useState(false);
  const [direction, setDirection] = useState<CompareDirection>("back");
  const [forecast, setForecast] = useState<InstanceForecast | null>(null);
  const [day, setDayState] = useState<string>(() => rememberedDay ?? daysAgo(DEFAULT_PRESET_DAYS));
  // The day actually replayed. Equal to `day` except while the scrubber is moving, when it waits for a rest.
  const [fetchDay, setFetchDay] = useState<string>(day);
  const restTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [changeDays, setChangeDays] = useState<InstanceChangeDays | null>(() => changeDaysCache.get(instanceId) ?? null);
  const [status, setStatus] = useState<CompareMode["status"]>("idle");
  const [snapshot, setSnapshot] = useState<InstanceSnapshot | null>(null);
  const [follows, setFollows] = useState<CompareFollows>("now");
  const [attempt, setAttempt] = useState(0);
  // Every request carries a generation; only the newest may land. A slow answer for a day the reader has
  // already moved past would otherwise paint the wrong past over the right one.
  const generation = useRef(0);

  useEffect(() => {
    if (!active || !enabled || direction !== "ahead") return;
    const key = `${instanceId}|${fetchDay}`;
    const cached = forecastCache.get(key);
    if (cached) {
      generation.current++;
      setForecast(cached);
      setSnapshot(cached.ahead);
      setStatus("ready");
      return;
    }
    const token = getAccessToken();
    const at = endOfDay(fetchDay);
    if (!token || Number.isNaN(at.getTime())) return;
    const mine = ++generation.current;
    setStatus("loading");
    api.instanceAhead(token, instanceId, at.toISOString())
      .then((f) => {
        forecastCache.set(key, f);
        if (generation.current !== mine) return;
        setForecast(f);
        setSnapshot(f.ahead);
        setStatus("ready");
      })
      .catch(() => {
        if (generation.current === mine) setStatus("error");
      });
  }, [active, enabled, direction, instanceId, fetchDay, attempt, api, getAccessToken]);

  useEffect(() => {
    if (!active || !enabled || direction !== "back") return;
    const key = `${instanceId}|${fetchDay}`;
    const cached = snapshotCache.get(key);
    if (cached) {
      generation.current++; // an older request still in flight must not land over this
      setSnapshot(cached);
      setStatus("ready");
      return;
    }
    const token = getAccessToken();
    const at = endOfDay(fetchDay);
    if (!token || Number.isNaN(at.getTime())) return;
    const mine = ++generation.current;
    setStatus("loading");
    api.instanceAsOf(token, instanceId, at.toISOString())
      .then((snap) => {
        snapshotCache.set(key, snap);
        if (generation.current !== mine) return;
        setSnapshot(snap);
        setStatus("ready");
      })
      .catch(() => {
        if (generation.current === mine) setStatus("error");
      });
  }, [active, enabled, direction, instanceId, fetchDay, attempt, api, getAccessToken]);

  // The ticks, once per dial per session. Best-effort: without them the scrubber still works as a plain
  // slider, and the presets and date field don't need them at all.
  useEffect(() => {
    if (!active || !enabled || direction !== "back") return;
    const cached = changeDaysCache.get(instanceId);
    if (cached) {
      setChangeDays(cached);
      return;
    }
    setChangeDays(null); // another dial's ticks must never sit on this one's track
    const token = getAccessToken();
    if (!token) return;
    let live = true;
    // Through a promise, so even a synchronous failure (an older client without the method) is just "no ticks".
    Promise.resolve()
      .then(() => api.instanceChangeDays(token, instanceId))
      .then((days) => {
        changeDaysCache.set(instanceId, days);
        if (live) setChangeDays(days);
      })
      .catch(() => { /* ticks are a nicety */ });
    return () => {
      live = false;
    };
  }, [active, enabled, direction, instanceId, api, getAccessToken]);

  useEffect(() => () => {
    if (restTimer.current) clearTimeout(restTimer.current);
  }, []);

  const setDay = useCallback((next: string) => {
    if (restTimer.current) {
      clearTimeout(restTimer.current);
      restTimer.current = null;
    }
    if (direction === "ahead") rememberedAheadDay = next;
    else rememberedDay = next;
    setDayState(next);
    setFetchDay(next);
  }, [direction]);

  const previewDay = useCallback((next: string) => {
    if (direction === "ahead") rememberedAheadDay = next;
    else rememberedDay = next;
    setDayState(next);
    if (restTimer.current) clearTimeout(restTimer.current);
    restTimer.current = setTimeout(() => {
      restTimer.current = null;
      setFetchDay(next);
    }, SCRUB_REST_MS);
  }, [direction]);

  const leave = useCallback(() => {
    generation.current++;
    setActive(false);
    setFollows("now");
    setSnapshot(null);
    setForecast(null);
    forecastCache.clear(); // the future moves as people act; the next look asks again
  }, []);

  const enter = useCallback((to: CompareDirection = "back") => {
    generation.current++;
    if (to !== direction) {
      setSnapshot(null);
      setForecast(null);
      setStatus("idle");
    }
    setDirection(to);
    const start = to === "ahead"
      ? rememberedAheadDay ?? daysAhead(DEFAULT_AHEAD_DAYS)
      : rememberedDay ?? daysAgo(DEFAULT_PRESET_DAYS);
    setDayState(start);
    setFetchDay(start);
    setActive(true);
  }, [direction]);

  return {
    active: active && enabled,
    direction,
    changes: direction === "ahead" ? forecast?.changes ?? null : null,
    days: direction === "ahead" ? forecast?.days ?? null : null,
    day,
    status,
    snapshot,
    follows,
    enter,
    leave,
    setDay,
    previewDay,
    setFollows,
    retry: useCallback(() => setAttempt((n) => n + 1), []),
    changeDays,
  };
}
